import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';

import { errorMessage, normalizeApiError } from '../services/apiError';
import { signalInsufficientCredits } from '../services/creditGateEvents';
import { projectService } from '../services/projectService';
import { getCreditShortfall } from '../utils/creditGate';
import { isIOSApp } from '../utils/platformDetection';

import { AppStoreRestrictionModal } from './AppStoreRestrictionModal';
import { ModalBackdrop } from './ModalBackdrop';
import { ModalContent } from './ModalContent';
import { ModalHeader } from './ModalHeader';
import { useToast } from './Toast';

type DeployStageButtonProps = {
  codeReady: boolean;
  hasDesignPage: boolean;
  hasCompletedTask: boolean;
  onUpgradeRequired: () => void;
  projectId: string | null;
  userTier?: string | null;
};

export const getDeployErrorMessage = (error: unknown): string => errorMessage(
  error,
  'We couldn’t submit the deployment. Check the project status and try again.',
);

export const notifyStageDeploySubmitted = () => {
  window.dispatchEvent(new Event('trigger_stage_deploy'));
  window.dispatchEvent(new Event('refresh_tasks'));
};

export const isFreeTier = (tier?: string | null) => (tier || 'free').toLowerCase() === 'free';

const getDeployDisabledReason = (
  projectId: string | null,
  isDeploying: boolean,
  codeReady: boolean,
  hasCompletedTask: boolean,
) => {
  if (!projectId) return 'Open a project before deploying';
  if (isDeploying) return 'Submitting stage deployment';
  if (!codeReady) return 'Generate code before deploying';
  if (!hasCompletedTask) return 'Complete at least one task before deploying';

  return null;
};

export const submitStageDeploy = async (
  projectId: string,
  toast: ReturnType<typeof useToast>,
) => {
  const result = await projectService.triggerStageDeploy(projectId).catch((error: unknown) => {
    const normalized = normalizeApiError(error);

    if (normalized.status !== 402 && normalized.code !== 'INSUFFICIENT_CREDITS') throw error;
    signalInsufficientCredits();

    return null;
  });

  if (!result) return;

  if (result.status !== 'ok') {
    if (result.error_code === 'INSUFFICIENT_CREDITS') {
      signalInsufficientCredits(getCreditShortfall({
        required: result.required_credits,
        balance: result.available_credits,
      }));

      return;
    }
    toast.error(result.error_message || 'The deployment could not be submitted. Review the project status and try again.');

    return;
  }

  toast.success('Stage deployment submitted');
  notifyStageDeploySubmitted();
};

const startStageDeploy = (
  projectId: string,
  toast: ReturnType<typeof useToast>,
  setIsDeploying: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setIsDeploying(true);
  void submitStageDeploy(projectId, toast)
    .catch((error: unknown) => toast.error(getDeployErrorMessage(error)))
    .finally(() => setIsDeploying(false));
};

function DesignRequiredModal({ onClose }: { onClose: () => void }) {
  return createPortal(
    <ModalBackdrop isVisible onClick={onClose}>
      <ModalContent onClose={onClose}>
        <ModalHeader
          title="First create at least one design"
          subtitle="Create one design page before deploying this product to stage."
          onClose={onClose}
        />
      </ModalContent>
    </ModalBackdrop>,
    document.body,
  );
}

export function DeployStageButton({
  codeReady,
  hasDesignPage,
  hasCompletedTask,
  onUpgradeRequired,
  projectId,
  userTier,
}: DeployStageButtonProps) {
  const toast = useToast();
  const [isDeploying, setIsDeploying] = useState(false);
  const [designRequiredOpen, setDesignRequiredOpen] = useState(false);
  const [iosRestrictionOpen, setIosRestrictionOpen] = useState(false);
  const disabledReason = getDeployDisabledReason(projectId, isDeploying, codeReady, hasCompletedTask);
  const isDeployDisabled = Boolean(disabledReason);

  const blockDeploy = useCallback((): boolean => {
    if (isFreeTier(userTier)) {
      onUpgradeRequired();

      return true;
    }
    if (!hasDesignPage) {
      setDesignRequiredOpen(true);

      return true;
    }

    return false;
  }, [hasDesignPage, onUpgradeRequired, userTier]);

  const handleDeploy = useCallback(() => {
    // iOS App Store guideline 2.5.2: block deploy from the native app
    if (isIOSApp()) {
      setIosRestrictionOpen(true);

      return;
    }
    if (!projectId || isDeploying) return;
    if (disabledReason) return;
    if (blockDeploy()) return;
    startStageDeploy(projectId, toast, setIsDeploying);
  }, [blockDeploy, disabledReason, isDeploying, projectId, toast]);

  return (
    <>
      <button
        aria-label="Deploy develop branch to stage"
        className="btn-deploy-stage-nav"
        disabled={isDeployDisabled}
        onClick={handleDeploy}
        title={disabledReason || 'Deploy develop branch to stage'}
        type="button"
      >
        <span className="btn-deploy-stage-nav__icon" aria-hidden="true">
          <i className={`fa-solid ${isDeploying ? 'fa-spinner fa-spin' : 'fa-cloud-arrow-up'}`} />
        </span>
        <span className="btn-deploy-stage-nav__main">Deploy</span>
      </button>
      {designRequiredOpen && <DesignRequiredModal onClose={() => setDesignRequiredOpen(false)} />}
      <AppStoreRestrictionModal
        isOpen={iosRestrictionOpen}
        onClose={() => setIosRestrictionOpen(false)}
      />
    </>
  );
}
